import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { cn, FileType } from '@restworld/utils-common';

import { Heading } from '@restworld/ui-ds-base';
import { CardUploadIcon } from './svg';
import { readFileIntoMemory } from './read-file-into-memory';

// Both CV flows that use this card load the onboarding-page namespace. Passed as an option, not as
// an `onboarding-page:` prefix: i18next reads a key containing a comma as natural language and
// never splits the prefix off, so the raw key would be shown.
const FILE_UNREADABLE_MESSAGE_KEY = 'we_could_not_open_this_file_from_your_phone,save_it_on_the_phone_and_try_again';

// The upload limit both CV flows enforce. A file above it is handed on untouched and never read:
// the flows refuse it by size anyway, and reading hundreds of MB into memory on a low-end phone
// is how a tab gets killed.
const MAX_BYTES_READ_INTO_MEMORY = 20 * 1_000_000;

interface IUploadCardProps {
  fileType?: string | string[];
  setUploadFiles: (value: FileType) => void;
  isError?: boolean;
  testId?: string;
}
export const UploadCard: React.FunctionComponent<IUploadCardProps> = ({
  fileType = ['.pdf'],
  setUploadFiles,
  isError,
  testId,
}) => {
  const acceptedFormats = useMemo(
    () =>
      typeof fileType === 'string' ? fileType : Array.isArray(fileType) ? fileType?.join(',') : AcceptedFileType.Pdf,
    [fileType]
  );
  const { t } = useTranslation();
  const [isUnreadable, setIsUnreadable] = useState(false);

  // Only the latest pick may land. Clearing the input lets the person pick again while a read is
  // still running, and without this a slow first read finishing after a fast second one would
  // replace the file they chose last with the one they changed their mind about.
  const latestPick = useRef(0);
  useEffect(
    () => () => {
      // Unmounted: nothing still reading may write into a card that is gone.
      latestPick.current += 1;
    },
    []
  );

  // The bytes are copied into memory here, at pick time, and only the copy is handed on. See
  // readFileIntoMemory for why: on Android the original is read lazily, when the request is sent,
  // and that read is what silently failed.
  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const picked = Array.from(input.files ?? []);
      // Lets the person pick the same file again after a failed read; the change event would not
      // fire otherwise.
      input.value = '';
      if (!picked.length) return;

      const pick = ++latestPick.current;
      const isStale = () => pick !== latestPick.current;

      try {
        const handedOn = await Promise.all(
          picked.map((file) => (file.size > MAX_BYTES_READ_INTO_MEMORY ? file : readFileIntoMemory(file)))
        );
        if (isStale()) return;
        setIsUnreadable(false);
        setUploadFiles(handedOn);
      } catch {
        if (isStale()) return;
        setIsUnreadable(true);
      }
    },
    [setUploadFiles]
  );

  const showError = isError || isUnreadable;
  return (
    <Wrapper data-cy={testId}>
      <div className="flex items-center justify-center w-full">
        <LabelStyled isError={showError} htmlFor="dropzone-file" data-cy="dropzone-file">
          <div className="flex flex-col items-center justify-center py-6 px-11 gap-2">
            <CardUploadIcon stroke={showError ? '#ba001f' : '#8e69ce'} />
            <p className="mb-2 text-sm text-black">
              <UnderLinedText isError={showError}>{t('common:click_here')}</UnderLinedText>{' '}
              {t('common:to_upload_your_file')}
            </p>
            <Heading
              as="span"
              size={6}
              weight="normal"
              color="muted"
              className="text-xs font-light"
              data-cy="upload-a-file-text"
            >
              {`${t('common:upload_a_file')}: ${acceptedFormats.replace(/,/g, ', ')} (20MB max)`}
            </Heading>
          </div>
          <Input
            type="file"
            id="dropzone-file"
            hidden
            onChange={handleChange}
            accept={acceptedFormats}
            data-cy="upload-a-file-input"
          />
        </LabelStyled>
      </div>
      {isUnreadable && (
        <p role="alert" className="p-2 text-xs text-red-700" data-cy="upload-cv-file-unreadable-error">
          {t(FILE_UNREADABLE_MESSAGE_KEY, { ns: 'onboarding-page' })}
        </p>
      )}
    </Wrapper>
  );
};

export default UploadCard;
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full', className)} {...props} />
);
const Input = ({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cn('', className)} {...props} />
);
const AcceptedFileType = {
  Text: '.txt',
  Gif: '.gif',
  Jpeg: '.jpg',
  Png: '.png',
  Doc: '.doc',
  Pdf: '.pdf',
  AllImages: 'image/*',
  AllVideos: 'video/*',
  AllAudios: 'audio/*',
};
const LabelStyled = ({ isError, className, ...props }: any) => (
  <label
    className={cn(
      'flex flex-col items-center justify-center w-full h-full border-2 border-primary-800 border-dashed rounded-[1rem] cursor-pointer bg-primary-50',
      isError ? 'border-red-600 bg-red-50' : '',
      className
    )}
    {...props}
  />
);
const UnderLinedText = ({ isError, className, ...props }: any) => (
  <span
    className={cn(
      'font-semibold text-primary-800 underline underline-offset-4',
      isError ? 'text-red-600' : 'text-primary-800',
      className
    )}
    {...props}
  />
);
